"use client";

import React from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { buildGoogleSignInUrl } from "../services/auth.api";
import { getClientRegion } from "@/services/region";
import { sanitizeReturnPath } from "../utils/loginRedirect";
import type { AccountType } from "../types";

type Props = {
  /**
   * Which account context to sign into. Google never *creates* a teacher
   * account - that is admin-provisioned - so "teacher" only works for someone
   * who already has one.
   */
  accountType?: AccountType;
  referralCode?: string | null;
  /**
   * Path on this site to return to once sign-in completes. Defaults to the
   * page the button is on, which is what the free-trial wizard wants.
   */
  returnTo?: string | null;
  disabled?: boolean;
  /** Which wording to use - signing in, signing up, or the neutral default. */
  variant?: "continue" | "signin" | "signup";
  className?: string;
};

/**
 * Whether the Google sign-in buttons are switched on (GOOGLE_SIGNIN_ENABLED,
 * inlined at build time - see next.config.mjs). Exported so a call site can
 * hide what it renders *around* the button too, such as the "or" divider,
 * rather than leaving it orphaned when the button renders nothing.
 */
export function isGoogleSignInEnabled(): boolean {
  return process.env.GOOGLE_SIGNIN_ENABLED !== "false";
}

/**
 * "Continue with Google".
 *
 * Deliberately an anchor, not a button that fetches: sign-in is a Socialite
 * redirect flow, so the browser must navigate the top-level document to the
 * API and follow it out to Google and back. An XHR here would be blocked by
 * CORS at Google's end and would lose the redirect chain.
 *
 * Renders nothing when GOOGLE_SIGNIN_ENABLED is "false", so every call site can
 * mount it unconditionally. It is the same variable that switches the backend
 * endpoints off; here it is inlined at build time (see next.config.mjs).
 */
export default function GoogleSignInButton({
  accountType = "student",
  referralCode = null,
  returnTo = null,
  disabled = false,
  variant = "continue",
  className = "",
}: Props) {
  const t = useTranslations("auth.google");
  const locale = useLocale();
  const pathname = usePathname();

  if (!isGoogleSignInEnabled()) {
    return null;
  }

  // Round-tripping the *current* path keeps a guest who started in the booking
  // wizard landing back in it rather than on a dashboard. sanitizeReturnPath
  // rejects auth routes, so signing in from /login or /register sends nothing
  // and the callback falls back to its own sensible destination - without it,
  // a successful sign-in would ask the frontend to return to the login page it
  // just came from.
  const safeReturnTo = sanitizeReturnPath(returnTo ?? pathname);

  const href = buildGoogleSignInUrl({
    accountType,
    region: getClientRegion(pathname),
    referralCode,
    returnTo: safeReturnTo,
    locale,
  });

  const label =
    variant === "signin" ? t("sign_in_with") : variant === "signup" ? t("sign_up_with") : t("continue_with");

  return (
    <a
      href={disabled ? undefined : href}
      aria-disabled={disabled}
      data-testid="google-signin-button"
      className={[
        "flex w-full items-center justify-center gap-3 rounded-[10px] border border-border",
        "bg-card px-4 py-3 text-sm font-semibold text-text-secondary transition",
        "hover:bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
        disabled ? "pointer-events-none opacity-50" : "cursor-pointer",
        className,
      ].join(" ")}
    >
      <GoogleMark />
      <span>{label}</span>
    </a>
  );
}

/**
 * Google's mark, inline. Their branding terms require the official colours and
 * forbid recolouring it, so this is not themed and carries no currentColor.
 */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}
